import { CommonModule } from "@angular/common";
import { Component, inject, Input } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { MarkdownPipe } from "../../../core/pipes/markdown.pipe";
import { shouldRenderMarkdown as contentLooksLikeMarkdown } from "../../../core/utils/markdown.util";

@Component({
  selector: "app-tcg-story-detail-modal",
  standalone: true,
  imports: [CommonModule, MarkdownPipe],
  templateUrl: "./tcg-story-detail-modal.component.html",
  styleUrl: "./tcg-story-detail-modal.component.scss",
})
export class TcgStoryDetailModalComponent {
  activeModal = inject(NgbActiveModal);
  isActive: boolean = false;

  @Input() data: any;
  @Input() title: any = "";
  @Input() contentList: any = [];

  shouldRenderMarkdown(value: unknown, mapping?: string): boolean {
    return contentLooksLikeMarkdown(value, mapping);
  }

  /**
   * Align with expandable-accordion list parsing (`#` / `*` / `•` / numbered).
   * For acceptance criteria, also split blank-line-separated blocks into bullets
   * so multi-criterion plain text matches the accordion list look.
   */
  getProcessedContent(
    value: string | null | undefined,
    mapping?: string
  ): { type: "numbered" | "bullets" | "plain"; items: string[] } {
    if (!this.isValidString(value)) {
      return { type: "plain", items: [] };
    }

    const delimiter = this.getListDelimiter(value);
    if (delimiter === "#" || delimiter === "*" || delimiter === "•") {
      const items = this.splitOnMarker(value, delimiter);
      return this.maybeExpandAcceptanceCriteria(items, mapping);
    }
    if (delimiter === "number") {
      return { type: "numbered", items: this.splitOnNumberPrefixes(value) };
    }

    if (this.isAcceptanceCriteriaField(mapping)) {
      const paragraphs = this.splitOnBlankLines(value);
      if (paragraphs.length > 1) {
        return { type: "bullets", items: paragraphs };
      }
    }

    return { type: "plain", items: [value] };
  }

  private maybeExpandAcceptanceCriteria(
    items: string[],
    mapping?: string
  ): { type: "bullets"; items: string[] } {
    if (!this.isAcceptanceCriteriaField(mapping) || items.length !== 1) {
      return { type: "bullets", items };
    }
    const paragraphs = this.splitOnBlankLines(items[0]);
    return {
      type: "bullets",
      items: paragraphs.length > 1 ? paragraphs : items,
    };
  }

  private isAcceptanceCriteriaField(mapping?: string): boolean {
    if (!mapping) return false;
    const key = mapping.toLowerCase().replace(/[_-]/g, "");
    return key === "acceptancecriteria" || key === "criteria";
  }

  private splitOnBlankLines(value: string): string[] {
    return value
      .split(/\n\s*\n/)
      .map((item) => this.cleanListItem(item))
      .filter((item) => item.length > 0);
  }

  private isValidString(value: string | null | undefined): value is string {
    return !!(value && typeof value === "string");
  }

  /** Item boundary: start of string, comma, or newline. */
  private readonly itemBoundary = "(?:^|,|\\n|\\r\\n)\\s*";

  private getListDelimiter(
    value: string
  ): "#" | "*" | "•" | "number" | null {
    const hashCount = this.countPrefixes(value, "#");
    const starCount = this.countPrefixes(value, "\\*");
    const bulletCount = this.countPrefixes(value, "•");
    const numberCount = this.countNumberPrefixes(value);
    const trimmed = value.trim();

    // Prefer numbered outlines even when nested `*` bullets exist under each item.
    if (numberCount >= 2 && /^\d/.test(trimmed)) {
      return "number";
    }
    if (hashCount > 0 && (trimmed.startsWith("#") || hashCount >= 2)) {
      return "#";
    }
    if (starCount > 0 && (trimmed.startsWith("*") || starCount >= 2)) {
      return "*";
    }
    if (bulletCount > 0 && (trimmed.startsWith("•") || bulletCount >= 2)) {
      return "•";
    }
    return null;
  }

  private countPrefixes(value: string, marker: string): number {
    const matches = value.match(
      new RegExp(`${this.itemBoundary}${marker}`, "g")
    );
    return matches ? matches.length : 0;
  }

  /**
   * Top-level numbered markers only (no leading indent), requiring `.` or `)`.
   */
  private countNumberPrefixes(value: string): number {
    const matches = value.match(/(?:^|\n)\d+[.)]\s+/g);
    return matches ? matches.length : 0;
  }

  private splitOnMarker(value: string, marker: "#" | "*" | "•"): string[] {
    return value
      .split(marker)
      .map((item) => this.cleanListItem(item))
      .filter((item) => item.length > 0);
  }

  private splitOnNumberPrefixes(value: string): string[] {
    // Top-level numbers only — keep nested bullets inside each numbered chunk.
    return value
      .split(/(?:^|\n)\d+[.)]\s+/m)
      .map((item) => this.cleanListItem(item))
      .filter((item) => item.length > 0);
  }

  private cleanListItem(item: string): string {
    return item.replace(/^[\s,]+|[\s,]+$/g, "").trim();
  }
}
